// Admin CRUD for what the driver-portal incident report form asks for --
// same shape as VehicleCheckItemsSettings.tsx, but with a "requirement"
// select (mandatory/optional/hidden) instead of a plain active/inactive
// toggle, since a field being off the form is meaningfully different from
// it being on but not required.
import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/components/ui/use-toast";
import { supabase } from "@/lib/supabase";
import { Plus, Save, X, Edit, Trash2, ListChecks } from "lucide-react";

type Requirement = "mandatory" | "optional" | "hidden";

interface FieldSetting {
  id: string;
  incident_category: string;
  field_key: string;
  label: string;
  requirement: Requirement;
  sort_order: number;
  is_active: boolean;
}

const INCIDENT_CATEGORIES = ["vehicle_collision", "property_damage", "single_vehicle", "other"];
const REQUIREMENTS: Requirement[] = ["mandatory", "optional", "hidden"];

const emptyNewForm = { incident_category: "vehicle_collision", field_key: "", label: "", requirement: "optional" as Requirement, sort_order: 100 };

const requirementBadge: Record<Requirement, string> = {
  mandatory: "bg-red-900/40 text-red-300 border border-red-700/50",
  optional: "bg-gray-700/50 text-gray-300 border border-gray-600",
  hidden: "bg-gray-900/60 text-gray-500 border border-gray-800",
};

const IncidentReportFieldSettings: React.FC = () => {
  const [items, setItems] = useState<FieldSetting[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<Partial<FieldSetting>>({});
  const [showAddForm, setShowAddForm] = useState(false);
  const [newForm, setNewForm] = useState(emptyNewForm);

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("incident_report_field_settings")
      .select("*")
      .order("incident_category", { ascending: true })
      .order("sort_order", { ascending: true });

    if (error) {
      toast({ title: "Error", description: "Failed to load incident field settings: " + error.message, variant: "destructive" });
    } else {
      setItems(data || []);
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const startEdit = (item: FieldSetting) => {
    setEditingId(item.id);
    setEditForm({ ...item });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({});
  };

  const saveEdit = async () => {
    if (!editingId || !editForm.label?.trim() || !editForm.incident_category?.trim()) {
      toast({ title: "Error", description: "Incident type and label are required", variant: "destructive" });
      return;
    }
    setSaving(true);
    const { error } = await supabase
      .from("incident_report_field_settings")
      .update({
        incident_category: editForm.incident_category.trim(),
        label: editForm.label.trim(),
        requirement: editForm.requirement ?? "optional",
        sort_order: editForm.sort_order ?? 100,
      })
      .eq("id", editingId);

    if (error) {
      toast({ title: "Error", description: "Failed to update: " + error.message, variant: "destructive" });
    } else {
      toast({ title: "Saved", description: `"${editForm.label}" updated` });
      setEditingId(null);
      setEditForm({});
      load();
    }
    setSaving(false);
  };

  const addItem = async () => {
    const incident_category = newForm.incident_category.trim();
    const label = newForm.label.trim();
    const keySource = newForm.field_key.trim() || label;
    const field_key = keySource
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "");

    if (!incident_category || !label || !field_key) {
      toast({ title: "Error", description: "Incident type and label are required", variant: "destructive" });
      return;
    }
    if (items.some((i) => i.incident_category === incident_category && i.field_key === field_key)) {
      toast({ title: "Error", description: "A field with this key already exists for this incident type", variant: "destructive" });
      return;
    }

    setSaving(true);
    const { error } = await supabase.from("incident_report_field_settings").insert({
      incident_category,
      field_key,
      label,
      requirement: newForm.requirement,
      sort_order: newForm.sort_order,
    });

    if (error) {
      toast({ title: "Error", description: "Failed to add: " + error.message, variant: "destructive" });
    } else {
      toast({ title: "Added", description: `"${label}" added` });
      setShowAddForm(false);
      setNewForm(emptyNewForm);
      load();
    }
    setSaving(false);
  };

  const deleteItem = async (item: FieldSetting) => {
    if (!confirm(`Delete "${item.label}"? This only affects future incident reports.`)) return;
    const { error } = await supabase.from("incident_report_field_settings").delete().eq("id", item.id);
    if (error) {
      toast({ title: "Error", description: "Failed to delete: " + error.message, variant: "destructive" });
    } else {
      toast({ title: "Deleted", description: `"${item.label}" removed` });
      load();
    }
  };

  return (
    <div className="rounded-xl border border-yellow-900/20 bg-black/20 p-4 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h4 className="font-medium text-foreground flex items-center gap-2">
            <ListChecks className="h-4 w-4 text-yellow-500" />
            Incident Report Fields
          </h4>
          <p className="text-xs text-muted-foreground mt-1">
            What the driver-portal incident report form asks for, per incident type -- mandatory fields must be
            filled in before a driver can submit, optional fields are shown but skippable, hidden fields don't
            appear at all.
          </p>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={() => setShowAddForm((s) => !s)}
          className="border-yellow-600 text-yellow-400 hover:bg-yellow-900"
        >
          <Plus className="w-4 h-4 mr-1" />
          Add Field
        </Button>
      </div>

      {showAddForm && (
        <div className="space-y-3 p-3 rounded-lg border border-gray-600 bg-gray-800/50">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label className="text-yellow-500 text-xs">Incident Type</Label>
              <Input
                value={newForm.incident_category}
                onChange={(e) => setNewForm((f) => ({ ...f, incident_category: e.target.value }))}
                list="incident-categories"
              />
              <datalist id="incident-categories">
                {INCIDENT_CATEGORIES.map((c) => (
                  <option key={c} value={c} />
                ))}
              </datalist>
            </div>
            <div className="md:col-span-2">
              <Label className="text-yellow-500 text-xs">Label (what the driver sees)</Label>
              <Input
                value={newForm.label}
                onChange={(e) => setNewForm((f) => ({ ...f, label: e.target.value }))}
                placeholder="e.g. Other driver's phone number"
              />
            </div>
          </div>
          <div className="flex items-end gap-3">
            <div className="w-40">
              <Label className="text-yellow-500 text-xs">Requirement</Label>
              <Select value={newForm.requirement} onValueChange={(v) => setNewForm((f) => ({ ...f, requirement: v as Requirement }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {REQUIREMENTS.map((r) => (
                    <SelectItem key={r} value={r}>
                      {r}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-32">
              <Label className="text-yellow-500 text-xs">Sort Order</Label>
              <Input
                type="number"
                value={newForm.sort_order}
                onChange={(e) => setNewForm((f) => ({ ...f, sort_order: parseInt(e.target.value, 10) || 100 }))}
              />
            </div>
            <div className="flex-1" />
            <Button size="sm" variant="ghost" onClick={() => { setShowAddForm(false); setNewForm(emptyNewForm); }}>
              <X className="w-4 h-4 mr-1" />
              Cancel
            </Button>
            <Button size="sm" onClick={addItem} disabled={saving} className="golden-button">
              <Save className="w-4 h-4 mr-1" />
              Add
            </Button>
          </div>
        </div>
      )}

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading...</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-muted-foreground">No incident report fields configured yet.</p>
      ) : (
        <div className="space-y-2">
          {items.map((item) =>
            editingId === item.id ? (
              <div key={item.id} className="space-y-3 p-3 rounded-lg border border-gray-600 bg-gray-800/50">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <Label className="text-yellow-500 text-xs">Incident Type</Label>
                    <Input
                      value={editForm.incident_category || ""}
                      onChange={(e) => setEditForm((f) => ({ ...f, incident_category: e.target.value }))}
                      list="incident-categories"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-yellow-500 text-xs">Label</Label>
                    <Input
                      value={editForm.label || ""}
                      onChange={(e) => setEditForm((f) => ({ ...f, label: e.target.value }))}
                    />
                  </div>
                </div>
                <div className="flex items-end gap-3">
                  <div className="w-40">
                    <Label className="text-yellow-500 text-xs">Requirement</Label>
                    <Select
                      value={editForm.requirement ?? "optional"}
                      onValueChange={(v) => setEditForm((f) => ({ ...f, requirement: v as Requirement }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {REQUIREMENTS.map((r) => (
                          <SelectItem key={r} value={r}>
                            {r}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="w-32">
                    <Label className="text-yellow-500 text-xs">Sort Order</Label>
                    <Input
                      type="number"
                      value={editForm.sort_order ?? 100}
                      onChange={(e) => setEditForm((f) => ({ ...f, sort_order: parseInt(e.target.value, 10) || 100 }))}
                    />
                  </div>
                  <div className="flex-1" />
                  <Button size="sm" variant="ghost" onClick={cancelEdit}>
                    <X className="w-4 h-4 mr-1" />
                    Cancel
                  </Button>
                  <Button size="sm" onClick={saveEdit} disabled={saving} className="golden-button">
                    <Save className="w-4 h-4 mr-1" />
                    Save
                  </Button>
                </div>
              </div>
            ) : (
              <div key={item.id} className="flex items-center justify-between gap-3 p-3 rounded-lg border border-gray-700 bg-gray-800/30">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xs uppercase tracking-wide text-yellow-500/80">{item.incident_category}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${requirementBadge[item.requirement]}`}>{item.requirement}</span>
                  </div>
                  <p className="text-sm text-foreground truncate">{item.label}</p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button size="sm" variant="ghost" onClick={() => startEdit(item)}>
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => deleteItem(item)} className="text-red-400 hover:text-red-300">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )
          )}
        </div>
      )}
    </div>
  );
};

export default IncidentReportFieldSettings;
